import invariant from 'tiny-invariant';
import { FC, PropsWithChildren, createContext, useContext, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { CURRENT_ORGANIZATION, CURRENT_SUBSCRIBER } from '@lib/queries';
import {
  CurrentOrganizationQuery,
  CurrentSubscriberFieldsFragment,
  CurrentSubscriberQuery,
  OrganizationFieldsFragment,
} from '@generated/types';
import { setUserId } from '@lib/gtag';

interface IAuthContext {
  loading: boolean;
  currentSubscriber: CurrentSubscriberFieldsFragment | undefined;
  currentOrganization: OrganizationFieldsFragment | undefined;
  isAuthorized: boolean;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: subscriberData, loading: subscriberLoading } = useQuery<CurrentSubscriberQuery>(
    CURRENT_SUBSCRIBER,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: organizationData, loading: organizationLoading } =
    useQuery<CurrentOrganizationQuery>(CURRENT_ORGANIZATION);

  useEffect(() => {
    if (typeof window !== 'undefined' && subscriberData?.currentSubscriber) {
      const { emailConsent, id } = subscriberData.currentSubscriber;
      sessionStorage.setItem('SUBSCRIBER_ID', id);
      if (emailConsent) {
        setUserId(id);
      }
    }
  }, [subscriberData]);

  useEffect(() => {
    if (organizationData?.currentOrganization) {
      const { id } = organizationData.currentOrganization;
      sessionStorage.setItem('ORGANIZATION_ID', id);
    }
  }, [organizationData]);

  return (
    <AuthContext.Provider
      value={{
        loading: (subscriberLoading && !subscriberData) || organizationLoading,
        currentSubscriber: subscriberData?.currentSubscriber as CurrentSubscriberFieldsFragment,
        currentOrganization: organizationData?.currentOrganization as OrganizationFieldsFragment,
        isAuthorized: !!subscriberData?.currentSubscriber?.role?.permissions.some(
          p => p.key === 'profile.organization.save'
        ),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): IAuthContext => {
  const context = useContext(AuthContext);
  invariant(
    context && Object.prototype.hasOwnProperty.call(context, 'isAuthorized'),
    'useAuthContext must be used within an AuthProvider'
  );
  return context;
};
